/**
 * Where this browser first came from — captured once, on the first page it
 * lands on, and handed to the API at signup.
 *
 * The question it answers is "which campaign produced this customer", and that
 * is a FIRST-touch question: by the time someone subscribes they have usually
 * arrived again through search or a bookmark, so reading the URL at signup time
 * would credit the last click and quietly wipe out every channel that actually
 * does the work. Hence: written once, never overwritten, on the same 730-day
 * cookie horizon as `kv_did` (see `device-id.ts` for why that window).
 *
 * What is deliberately NOT captured:
 *
 * - The query string. `landingPath` is `location.pathname` only — a capability
 *   URL carries its secret in the path or the query, and this value is stored
 *   server-side and read back by staff.
 * - The path at all, on a capability URL. `isAnalyticsExcludedPath` decides,
 *   and the rest of the touch (utm, referrer host) is still recorded, because
 *   those are not the secret.
 * - The full referrer. Only its host: the path of the page someone came from
 *   can itself be private, and the host is what a channel report needs.
 */
import { reportHandledException } from '@/lib/error-reporting'
import { isAnalyticsExcludedPath } from '../analytics/excluded-paths'

export const FIRST_TOUCH_COOKIE = 'kv_ft'

/** Same horizon as `kv_did`; the two are only useful together. */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 730

/** Per-field ceiling, matching the API's column widths so nothing is silently truncated later. */
const MAX_FIELD_LENGTH = 256
/**
 * Ceiling for the whole serialized cookie. Browsers cap a cookie around 4 KB
 * and drop the write silently past it — losing the touch entirely — so an
 * absurd URL degrades to the fields that fit rather than to nothing.
 */
const MAX_COOKIE_LENGTH = 3_000

export interface FirstTouch {
  firstTouchAt: string
  landingPath?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  fbclid?: string
  gclid?: string
  referrerHost?: string
}

const UTM_PARAMS = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_content', 'utmContent'],
  ['utm_term', 'utmTerm'],
  ['fbclid', 'fbclid'],
  ['gclid', 'gclid'],
] as const

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  if (!match) return null
  try {
    return decodeURIComponent(match[1])
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'lib/identity/first-touch.ts' })
    // A mangled percent-sequence means the stored touch is unreadable. Treat it
    // as absent so this visit can write a fresh one, rather than throwing inside
    // whatever effect asked for it.
    return null
  }
}

function trimField(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return trimmed.slice(0, MAX_FIELD_LENGTH)
}

function referrerHost(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const raw = document.referrer
  if (!raw) return undefined
  try {
    const { host } = new URL(raw)
    // Our own pages are not a channel; recording them would make every
    // multi-page visit look like a self-referral.
    if (typeof location !== 'undefined' && host === location.host) return undefined
    return trimField(host)
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'lib/identity/first-touch.ts' })
    return undefined
  }
}

/** Build the touch this page load represents, or `null` when there is nothing to record. */
function captureFirstTouch(): FirstTouch | null {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  const touch: FirstTouch = { firstTouchAt: new Date().toISOString() }

  for (const [param, field] of UTM_PARAMS) {
    const value = trimField(params.get(param))
    if (value) touch[field] = value
  }

  const host = referrerHost()
  if (host) touch.referrerHost = host

  if (!isAnalyticsExcludedPath(window.location.pathname, window.location.search)) {
    const path = trimField(window.location.pathname)
    if (path) touch.landingPath = path
  }

  // A bare timestamp is not a touch — everyone has one. Recording it would make
  // "arrived directly" indistinguishable from "we failed to capture anything",
  // and the cookie would then block the real touch on a later visit.
  const hasSignal = Object.keys(touch).some((key) => key !== 'firstTouchAt')
  return hasSignal ? touch : null
}

/**
 * Record this browser's first touch if it has none, then return whatever is
 * stored (`null` when nothing ever was).
 *
 * Safe to call on every page load: an existing cookie short-circuits before
 * anything is read from the URL, which is what makes "first" true.
 */
export function getOrCaptureFirstTouch(): FirstTouch | null {
  if (typeof document === 'undefined') return null

  const stored = readStoredFirstTouch()
  if (stored) return stored

  const touch = captureFirstTouch()
  if (!touch) return null

  try {
    const encoded = encodeURIComponent(JSON.stringify(touch))
    if (encoded.length <= MAX_COOKIE_LENGTH) {
      const secure =
        typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
      document.cookie = `${FIRST_TOUCH_COOKIE}=${encoded}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`
    }
  } catch (cause) {
    reportHandledException(cause, { source: 'lib/identity/first-touch.ts' })
    // Cookies blocked. The touch is still returned so THIS page load can report
    // it if the visitor signs up right now; it simply will not survive a reload.
    console.warn('[identity] could not persist the first touch:', cause)
  }
  return touch
}

/**
 * Read the stored touch without capturing one.
 *
 * The cookie is writable by any script on the origin, so every field is
 * re-validated on the way out: the value is sent to our API and stored against
 * an account, and an unchecked read is a path for arbitrary text to get there.
 */
export function readStoredFirstTouch(): FirstTouch | null {
  const raw = readCookie(FIRST_TOUCH_COOKIE)
  if (!raw) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'lib/identity/first-touch.ts' })
    return null
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null

  const source = parsed as Record<string, unknown>
  const at = trimField(typeof source.firstTouchAt === 'string' ? source.firstTouchAt : undefined)
  if (!at || Number.isNaN(Date.parse(at))) return null

  const touch: FirstTouch = { firstTouchAt: at }
  for (const field of [
    'landingPath',
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'utmContent',
    'utmTerm',
    'fbclid',
    'gclid',
    'referrerHost',
  ] as const) {
    const value = source[field]
    const clean = trimField(typeof value === 'string' ? value : undefined)
    if (clean) touch[field] = clean
  }

  const hasSignal = Object.keys(touch).some((key) => key !== 'firstTouchAt')
  return hasSignal ? touch : null
}
